/**
 * LBR2-D2 careers-page — PUBLIC job-openings list (`/jobopenings`).
 * Unauthenticated. Lists the tenant's OPEN openings (tenant resolved server-side
 * from hostname). Each card links to the opening detail + apply page.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Briefcase, Clock, ArrowRight, AlertCircle } from 'lucide-react';
import { CareersChrome } from './CareersChrome';
import { listOpenings, type Opening } from './careersApi';
import { formatEmploymentType, formatExperienceRange } from './careersFormat';

export function JobOpeningsPage() {
  const [openings, setOpenings] = useState<Opening[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    listOpenings()
      .then((o) => {
        if (alive) setOpenings(o);
      })
      .catch((e) => {
        if (alive) {
          setError(e.message);
          setOpenings([]);
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <CareersChrome>
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-gray-900">Open positions</h1>
        <p className="mt-1 text-sm text-gray-500">
          Join our team — explore the roles we&apos;re hiring for.
        </p>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {openings === null ? (
        <div className="py-16 text-center text-gray-400">Loading positions…</div>
      ) : openings.length === 0 && !error ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white py-16 text-center">
          <Briefcase className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <p className="font-semibold text-gray-700">No open positions right now</p>
          <p className="mt-1 text-sm text-gray-400">Please check back soon.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {openings.map((o) => {
            const exp = formatExperienceRange(o.minExperienceYears, o.maxExperienceYears);
            return (
              <Link
                key={o.id}
                to={`/jobopenings/${o.id}`}
                className="group flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:border-[#f5870a] hover:shadow-md"
              >
                <h2 className="font-display text-lg font-bold text-gray-900 group-hover:text-[#f5870a]">
                  {o.jobTitle}
                </h2>
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                    <MapPin className="h-3 w-3" />
                    {o.location}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                    <Briefcase className="h-3 w-3" />
                    {formatEmploymentType(o.employmentType)}
                  </span>
                  {exp && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                      <Clock className="h-3 w-3" />
                      {exp}
                    </span>
                  )}
                </div>
                {o.requiredSkills?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {o.requiredSkills.slice(0, 4).map((s) => (
                      <span
                        key={s}
                        className="rounded-md bg-orange-50 px-2 py-0.5 text-xs font-medium text-[#b5610a]"
                      >
                        {s}
                      </span>
                    ))}
                    {o.requiredSkills.length > 4 && (
                      <span className="px-1 text-xs text-gray-400">+{o.requiredSkills.length - 4}</span>
                    )}
                  </div>
                )}
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#f5870a]">
                  View &amp; apply <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </CareersChrome>
  );
}

export default JobOpeningsPage;
